import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle } from "lucide-react";
import api from "../lib/api";

/**
 * Klockan i topplisten.
 *
 * Två fel rättades 6 augusti, och båda gjorde funktionen sämre än ingen alls.
 *
 * **Att öppna panelen markerade allt som läst.** En användare som klickade för
 * att se vad prickarna gällde förlorade i samma sekund möjligheten att hitta
 * tillbaka till dem: märket nollades, och sju notiser hen inte hunnit läsa såg
 * ut som lästa i notiscentret. Nu markeras en notis som läst när den öppnas,
 * vilket är vad användaren rimligen tror att ett klick betyder.
 *
 * **Knappen "Visa alla" låg utanför panelen.** Den renderades efter panelens
 * div men inuti samma fragment, alltså i topplistens eget flöde i stället för i
 * rullgardinen. Den ligger nu i panelen där den hör hemma.
 *
 * Fliken "Kräver åtgärd" är samma urval som notiscentrets, och tas ur samma
 * flagga i backend. Två definitioner av vad som kräver en åtgärd hade glidit
 * isär, och den svagare hade vunnit.
 *
 * Uppdateringen är polling var sextionde sekund. Designen föreslår WebSocket
 * eller SSE. En persistent kanal betyder anslutningshantering, återanslutning
 * och en ny felkälla i topplisten, som syns på varje sida i produkten.
 */

const timeAgo = (d) => {
  if (!d) return "";
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return "nyss";
  if (diff < 3600) return `${Math.floor(diff / 60)} min sedan`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} tim sedan`;
  const days = Math.floor(diff / 86400);
  return `${days} ${days === 1 ? "dag" : "dagar"} sedan`;
};

const PREVIEW = 5;

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [actionCount, setActionCount] = useState(0);
  const [tab, setTab] = useState("all");
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
      setActionCount(data.summary?.action_required || 0);
    } catch { /* en trasig klocka får aldrig fälla sidan under den */ }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(iv); document.removeEventListener("mousedown", onClick); };
  }, []);

  /* Öppnandet läser ingenting. Se filens rubrikkommentar. */
  const go = async (n) => {
    setOpen(false);
    if (!n.read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      if (n.requires_action) setActionCount((a) => Math.max(0, a - 1));
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* navigering går före */ }
    }
    if (n.link) navigate(n.link);
  };

  const shown = (tab === "action" ? items.filter((n) => n.requires_action && !n.read) : items)
    .slice(0, PREVIEW);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="notification-bell"
        className="relative w-10 h-10 rounded-lg flex items-center justify-center text-neutral-300 hover:text-white hover:bg-[#181818] transition-colors"
        aria-label="Notiser">
        <Bell size={19} />
        {unread > 0 && (
          <span className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#FF5C00] text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="sm:hidden fixed inset-0 z-[9998]" onClick={() => setOpen(false)} data-testid="notification-backdrop" />
          <div className="fixed top-14 left-2 right-2 w-auto sm:absolute sm:top-full sm:left-auto sm:right-0 sm:mt-2 sm:w-[360px] sm:max-w-[calc(100vw-16px)] rounded-2xl border border-[#2A2A2A] bg-[#161618] shadow-2xl z-[9999] overflow-hidden"
            data-testid="notification-panel">

            <div className="px-4 pt-3.5 pb-0">
              <div className="text-white font-semibold text-[15px] mb-2.5">Notiser</div>
              <div className="flex gap-4 border-b border-[#2A2A2A]">
                {[["all", "Alla", items.filter((n) => !n.read).length],
                  ["action", "Kräver åtgärd", actionCount]].map(([k, label, n]) => (
                  <button key={k} onClick={() => setTab(k)} data-testid={`bell-tab-${k}`}
                    className={`pb-2.5 text-[12.5px] font-medium border-b-2 transition-colors ${
                      tab === k ? "border-[#FF5C00] text-white" : "border-transparent text-neutral-400 hover:text-white"}`}>
                    {label}
                    {n > 0 && (
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                        k === "action" ? "bg-[#e5484d] text-white" : "bg-[#2A2A2A] text-neutral-300"}`}>{n}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="max-h-[50vh] overflow-y-auto">
              {shown.length === 0 ? (
                <div className="px-4 py-9 text-center text-neutral-500 text-[13.5px]">
                  {tab === "action" ? "Ingenting kräver något av dig." : "Inga notiser än."}
                </div>
              ) : shown.map((n) => (
                <button key={n.id} onClick={() => go(n)} data-testid={`bell-notif-${n.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-[#222] hover:bg-[#1c1c1e] transition-colors ${
                    n.read ? "" : "bg-[#FF5C00]/6"}`}>
                  <div className="flex items-start gap-2.5">
                    <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-[#FF5C00]"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[13.5px] text-white font-medium truncate">{n.title}</span>
                        {n.requires_action && !n.read && (
                          <AlertTriangle size={12} className="text-[#e5484d] shrink-0" />
                        )}
                      </div>
                      {n.body && <div className="text-[12.5px] text-neutral-400 mt-0.5 line-clamp-2">{n.body}</div>}
                      <div className="text-[11px] text-neutral-600 mt-1">{timeAgo(n.created_at)}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <button onClick={() => { setOpen(false); navigate("/notifications"); }}
              data-testid="notif-see-all"
              className="w-full px-4 py-3 border-t border-[#2A2A2A] text-[13px] text-[#FF7A2F] hover:text-white hover:bg-[#1c1c1e] transition-colors">
              Visa alla notiser
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
